import { useFormik } from 'formik';
import * as Yup from 'yup';
import { BasicEventInfoDocument } from '../../../../../../../shared/utils/eventInfo/basicEventInfo.graphql.generated';
import { EventWebsiteGuestDocument } from '../../../../../GuestSite.graphql.generated';
import { GetGuestsiteCoupleStoryAndQandasDocument } from '../../../widgets/CoupleStory/CoupleStory.graphql.generated';
import { GetGuestSiteScheduleDocument } from '../../../widgets/ScheduleList/ScheduleList.graphql.generated';
import { GetEventCTAButtonsDataDocument } from '../../CtaButtons/CtaButtons.graphql.generated';
import { useJoinEventAsPersonWithPasswordMutation } from '@graphql/mutations/joinEvent.graphql.generated';
import { useEffect } from 'react';
import { withWindow } from '@shared/utils/withWindow';
import { buildAuthUrl, buildReturnPath } from '@shared/utils/navigation';
import { useToast } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { useApolloClient } from '@apollo/client';
import { hasGraphQLErrorMessage, NO_VALID_PASSWORD_PROVIDED } from '@shared/utils/checkGraphqlError';
import { addSuppressedError } from '@shared/utils/logger';

interface UnlockFields
  extends Readonly<{
    password: string;
  }> {}

interface UseUnlockControllerProps {
  eventId: string;
  onUnlockSuccess: () => void;
}

export function useUnlockController({ eventId, onUnlockSuccess }: UseUnlockControllerProps) {
  const { toast } = useToast();
  const [joinEvent] = useJoinEventAsPersonWithPasswordMutation();
  const { tq } = useTranslation('guestUnlock');
  const apolloClient = useApolloClient();

  const formik = useFormik<UnlockFields>({
    initialValues: {
      password: '',
    },
    validationSchema: Yup.object<UnlockFields>({
      password: Yup.string()
        .trim()
        .required(tq`Password’s required`),
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async values => {
      let hasUnlocked = false;
      try {
        const responseJoinEvent = await joinEvent({
          variables: {
            eventId,
            payload: { eventPassword: values.password.trim() },
          },
          refetchQueries: () => [
            BasicEventInfoDocument,
            GetGuestsiteCoupleStoryAndQandasDocument,
            GetEventCTAButtonsDataDocument,
            EventWebsiteGuestDocument,
            GetGuestSiteScheduleDocument,
            // Do NOT add 'GuestPageContainerBySlug' here — see cache.evict below.
          ],
          awaitRefetchQueries: true,
        });
        hasUnlocked = !!responseJoinEvent?.data?.joinEventAsPersonWithPassword?.hasUnlocked;
        if (hasUnlocked) {
          // Evict all pageContainerBySlug cache entries so that any mounted
          // GuestPageContainerBySlug query sees a cache miss and refetches
          // with its own (valid) variables. This replaces the string-based
          // refetchQueries approach ('GuestPageContainerBySlug') which is
          // unsafe — Apollo's string matching picks up ALL registered
          // ObservableQuery instances with that operation name, including
          // skipped ones with empty variables, causing spurious network errors.
          apolloClient.cache.evict({ fieldName: 'pageContainerBySlug' });
          apolloClient.cache.gc();
          onUnlockSuccess();
        }
      } catch (error) {
        if (hasGraphQLErrorMessage(error, NO_VALID_PASSWORD_PROVIDED)) {
          addSuppressedError(error, { context: 'UnlockForm', operation: 'onSubmit', eventId });
        }

        // Only show "wrong password" if the mutation itself failed.
        // If hasUnlocked is true, the unlock succeeded but a refetch
        // errored (e.g. network blip) — swallow it and let cache
        // eviction handle data freshness.
        if (!hasUnlocked) {
          formik.setFieldError('password', 'incorrect');
        }
      }
    },
  });

  const onSignInHandler = () => {
    withWindow(global => {
      global.location.href = buildAuthUrl('/login', buildReturnPath(global.location));
    });
  };

  const inputError = formik.errors.password;

  useEffect(() => {
    if (inputError === 'incorrect') {
      toast(tq`Wrong password. Double-check what you’ve typed, or try copying and pasting.`);
    }

    if (inputError && inputError !== 'incorrect') {
      toast(inputError);
    }
  }, [inputError, toast, tq]);

  return {
    formik,
    loading: formik.isSubmitting,

    inputError: formik.errors.password,
    onSignInHandler,
  };
}
